import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getWorkflowHistoryPage } from '../services/workflow-inspection.service';
import type { HistoryEvent } from '../types/workflow-inspection.types';
import { mergeHistoryEvents } from '../utils/history-events';
import { retryInspection } from '../utils/inspection-errors';

import { useSensitiveMask } from './use-sensitive-mask';

/** Events held client-side; the timeline needs the whole run, so it's loaded up front. */
export const HISTORY_EVENT_CAP = 2000;
/** Largest page the server allows — fewer round trips for the full load. */
const HISTORY_PAGE_SIZE = 1000;

/** Cached shape: ascending, de-duped RAW events plus whether the cap cut them off
 *  (masked on read by `useWorkflowHistory`'s `select`). */
export interface WorkflowHistoryData {
  events: HistoryEvent[];
  /** True when more history exists past `HISTORY_EVENT_CAP` (not loaded). */
  capped: boolean;
}

/**
 * Pages `GET /workflows/{id}/history` (ascending) until the end or the cap.
 * Empty pages with a token mean "keep paging" (`scanLimitReached`), so the loop
 * keys off the token, never the page size.
 */
async function loadHistory(workflowId: string, runId: string | null): Promise<WorkflowHistoryData> {
  let collected: HistoryEvent[] = [];
  let token: string | null = null;
  do {
    const page = await getWorkflowHistoryPage(workflowId, {
      runId,
      order: 'asc',
      pageSize: Math.min(HISTORY_PAGE_SIZE, HISTORY_EVENT_CAP - collected.length),
      nextPageToken: token,
    });
    collected = collected.concat(page.events);
    token = page.nextPageToken;
  } while (token && collected.length < HISTORY_EVENT_CAP);
  return { events: mergeHistoryEvents([], collected), capped: token !== null };
}

/**
 * Full event history for one execution, kept current by
 * `useWorkflowHistoryStream` (which appends into this same cache entry).
 * `staleTime: Infinity` — a closed history never changes and a live one is
 * fed by the stream, so a background refetch would only re-page everything.
 */
export function useWorkflowHistory(workflowId: string, runId: string | null, enabled = true) {
  const { mask } = useSensitiveMask([workflowId, runId]);
  return useQuery({
    queryKey: queryKeys.workflowInspection.history(workflowId, runId),
    queryFn: () => loadHistory(workflowId, runId),
    select: mask,
    enabled: enabled && !!workflowId,
    retry: retryInspection,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
